<template>
  <template v-if="link.children && !link.showInMenu && hasAnyChildPermission(link)">
    <CNavGroup :visible="checkLink(link) && hasAnyChildPermission(link)">
      <template #togglerContent>
        <CIcon v-if="link?.icon" custom-class-name="nav-icon" :icon="link?.icon" />
        <CNavItem>
          {{ $t('message.menu.' + link.name) }}
          <CButton v-if="link.name === 'requests' || link.name === 'orders_list'" color="info" shape="rounded-pill">{{
            link.name === 'requests' ? requestsMenuStatsCount : statsCount
          }}</CButton>

          <CButton
            v-if="(link.name === 'products-categories' || link.name === 'orders') && currentLang == 'de'"
            color="info"
            shape="rounded-pill"
            >{{ countStat('orders') }}</CButton
          >

          <CButton v-if="link.name === 'users' && countStat('inactive_users')" color="info" shape="rounded-pill">{{
            countStat('inactive_users') + countStat('customer_numbers_to_approve')
          }}</CButton>
        </CNavItem>
      </template>
      <template v-for="(item, idx) in link.children" :key="`child_${idx}`">
        <MenuItems :link="item" :parent-link="link" />
      </template>
    </CNavGroup>
  </template>
  <template v-else>
    <template v-if="link.showInMenu && hasPermission(link)">
      <CNavItem
        class="menu-nav-item"
        :class="{ 'menu-nav-item--loading': isItemLoading }"
        :href="
          router.resolve({
            name: link.name
          }).href
        "
        :active="checkLink(link)"
        @click="onMenuClick($event, link)"
      >
        <CIcon v-if="link?.icon" custom-class-name="nav-icon" :icon="link?.icon" />
        <div>
          <span v-html="$t('message.menu.' + link.name)"></span>&nbsp;<CButton
            v-if="countStat(link.name)"
            color="info"
            shape="rounded-pill"
            >{{ countStat(link.name) }}</CButton
          >
        </div>
        <div
          v-if="isItemLoading"
          class="menu-nav-item__loading-bar"
          aria-hidden="true"
        >
          <div class="menu-nav-item__loading-track">
            <div class="menu-nav-item__loading-indicator" />
          </div>
        </div>
      </CNavItem>
    </template>
  </template>
</template>

<script setup>
import { defineProps, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { isInLegacyGroup, isModuleEnabled } from '@/helpers/tenantModules'
import { hasPermission as checkPermission } from '@/helpers/permissions'
import requestsMenuRoutes from '@/router/requests'
import {
  loadingMenuRouteName,
  startMenuNavigationLoading,
  stopMenuNavigationLoading,
} from '@/composables/menuNavigationLoading'

const router = useRouter()
const store = useStore()

const REQUESTS_ROUTE_PARENT = {
  name: 'requests',
  meta: {
    moduleSlug: 'product-trainings-request'
  }
}

const role = computed(() => store.getters.getAdminRole)
const requestStats = computed(() => store.getters.getRequestStats || {})

const statsCount = computed(() => {
  return Object.entries(requestStats.value).reduce((sum, [key, value]) => {
    if (key === 'orders' || key === 'inactive_users' || key === 'customer_numbers_to_approve' || key === 'approve_indirect') {
      return sum
    }

    return sum + Number(value || 0)
  }, 0)
})

const requestsMenuStatsCount = computed(() => {
  const stats = requestStats.value
  if (!stats || typeof stats !== 'object') {
    return 0
  }

  let sum = 0
  for (const child of requestsMenuRoutes) {
    if (!child.showInMenu) continue
    if (!menuItemHasAccess(child, REQUESTS_ROUTE_PARENT)) continue
    const statKey = child.name === 'salespromotions' ? 'salespromotions' : child.name
    sum += Number(stats[statKey] || 0)
  }
  return sum
})

const props = defineProps({
  link: Object,
  parentLink: Object
})

const isItemLoading = computed(
  () => loadingMenuRouteName.value === props.link?.name
)

const checkLink = (link) => {
  if (link.children?.length > 0) {
    return link.children.some((child) => checkLink(child))
  }
  return link.name === router.currentRoute.value.name
}

const currentLang = computed(() => localStorage.getItem('lang'))

function getModuleSlugFromRoute(link, parentLink = null) {
  if (link.meta?.moduleSlug) return link.meta.moduleSlug
  if (parentLink?.meta?.moduleSlug) return parentLink.meta.moduleSlug

  const routeName = link.name || ''
  const modulesBySlug = store.getters.modulesBySlug || {}

  if (modulesBySlug[routeName]) return routeName

  const baseName = routeName.split(/[_-]/)[0]
  if (modulesBySlug[baseName]) return baseName

  return routeName
}

function checkRolePermission(link) {
  if (!link.meta?.requiredRole) return true
  if (!role.value) return false

  return role.value.some((r) => {
    const roleName = r?.name || r
    return link.meta.requiredRole.includes(roleName)
  })
}

function checkTenantPermission(link, moduleSlug) {
  const allowedTenantGroups = link.meta?.allowedTenant || []
  const checkModuleConfig = link.meta?.checkModuleConfig

  if (allowedTenantGroups.length > 0) {
    return allowedTenantGroups.some((group) => isInLegacyGroup(group))
  }

  if (checkModuleConfig && moduleSlug) {
    if (!isModuleEnabled(moduleSlug)) return false
    const configValue = store.getters.getModuleConfig(moduleSlug, checkModuleConfig, null)
    return configValue === true
  }

  return isModuleEnabled(moduleSlug)
}

function checkUserPermission(moduleSlug) {
  const userPermissions = store.getters.userPermissions || []
  if (userPermissions.length === 0) return true

  const moduleData = store.getters.modulesBySlug?.[moduleSlug]
  if (!moduleData) return true

  return userPermissions.includes(moduleData.id)
}

function menuItemHasAccess(link, parentLink = null) {
  if (!checkRolePermission(link)) return false

  if (link.meta?.requiredPermission) {
    if (!checkPermission(link.meta.requiredPermission)) {
      return false
    }
  }

  const moduleSlug = getModuleSlugFromRoute(link, parentLink)
  if (!checkTenantPermission(link, moduleSlug)) return false

  return checkUserPermission(moduleSlug)
}

function hasPermission(link) {
  return menuItemHasAccess(link, props.parentLink)
}

function hasAnyChildPermission(parentLink) {
  if (!parentLink.children?.length) return true
  return parentLink.children.some((child) => hasPermission(child))
}

const countStat = (name) => {
  if (!requestStats.value || typeof requestStats.value !== 'object') {
    return null
  }

  const value = Number(requestStats.value[name] || 0)
  return value > 0 ? value : null
}

let isMenuNavigating = false
const MENU_NAVIGATION_LOCK_MS = 500

const clearMenuStorage = () => {
  localStorage.removeItem('id')
  localStorage.removeItem('email')
  localStorage.removeItem('name')
  localStorage.removeItem('username')
  localStorage.removeItem('wholesaler_dealer_id')
  localStorage.removeItem('sale_person_id')
  localStorage.removeItem('date_from')
  localStorage.removeItem('date_to')
  localStorage.removeItem('showAddress')
  localStorage.removeItem('showGroup')
  localStorage.removeItem('showPoints')
  localStorage.removeItem('group_id')
  localStorage.removeItem('role')
  localStorage.removeItem('company_name')
  localStorage.removeItem('selectedCategory')
  localStorage.removeItem('selectedSku')
}

const onMenuClick = async (event, link) => {
  event.preventDefault()
  event.stopPropagation()

  if (isMenuNavigating) {
    return
  }

  const current = router.currentRoute.value
  const isSameRoute = current.name === link.name

  if (isSameRoute && !current.query.page) {
    return
  }

  isMenuNavigating = true
  clearMenuStorage()
  startMenuNavigationLoading(link.name)

  try {
    await router.push({ name: link.name })
  } catch (error) {
    stopMenuNavigationLoading()
    // Ignore duplicate navigation errors from vue-router
    if (error?.name !== 'NavigationDuplicated' && error?.name !== 'NavigationFailure') {
      console.error(error)
    }
  } finally {
    window.setTimeout(() => {
      isMenuNavigating = false
    }, MENU_NAVIGATION_LOCK_MS)
  }
}
</script>

<style scoped lang="scss">
:deep(.menu-nav-item.nav-link),
:deep(a.menu-nav-item),
.menu-nav-item {
  position: relative;
}

.menu-nav-item__loading-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  pointer-events: none;
  z-index: 2;
}

.menu-nav-item__loading-track {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: rgba(50, 31, 219, 0.12);
}

.menu-nav-item__loading-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 35%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(50, 31, 219, 0.15),
    var(--cui-primary, #321fdb),
    rgba(50, 31, 219, 0.15)
  );
  animation: menu-nav-loading-slide 1.1s ease-in-out infinite;
}

@keyframes menu-nav-loading-slide {
  0% {
    transform: translateX(-120%);
  }

  100% {
    transform: translateX(320%);
  }
}
</style>
